<template>
	<template v-if="visible">
		<div v-if="openable && !open" class="fd-widget-group-closed" v-bind="$attrs">
			<a href="#" class="wg-label-close" @click.prevent="toggleOpen"><sym-icon name="plus-square-14"></sym-icon> {{ wgp.label }}</a>
		</div>
		<div v-if="!openable || open" class="fd-widget-group" :class="groupClass" :style="style" v-bind="$attrs"><div class="reset-gutter">
			<template v-if="openable">
				<a href="#" class="wg-label" @click.prevent="toggleOpen"><sym-icon name="minus-square-14"></sym-icon> {{ wgp.labelOpen ?? wgp.label }}</a>
			</template>
			<component v-for="(wg) in wgp.items" :is="componentOfPart(wg)" :part="partRef(wg)" :data="localData" :readonly="ro" :viewmode="vm"></component>
		</div></div>
	</template>
</template>

<script setup lang="ts">
import {computed, ref} from "vue";
import useContainer, {type ContainerProps, containerProps} from "@fd/containers/Container.ts";
import type {WidgetGroup} from "@t/fd/layout/WidgetGroup.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";

const uprops = defineProps({...containerProps});
const props = uprops as ContainerProps;

const wgp = computed<WidgetGroup>(() => props.part as WidgetGroup);
const {visible, componentOfPart, partRef, localData, ro, vm, clazz, style} = useContainer(props);

const open = ref<boolean>(initialOpenState());

const openable = computed<boolean>(() => {
	return !!wgp.value.label;
});

const groupClass = computed<string[]>(() => {
	let gcs:string[] = []
	let cz = clazz.value;
	if (Array.isArray(cz)) gcs.push(...cz);
	const separ = wgp.value.separation ?? "none";
	if (separ !== "none") {
		gcs.push("wg-separ-" + separ);
	}
	if (openable.value) {
		gcs.push("wg-with-label");
	}
	return gcs;
});

function initialOpenState() {
	if (wgp.value.save) {
		const state = sessionStorage.getItem(wgp.value.save);		// open|hidden
		return "open" === state;
	}
	return false;
}

function toggleOpen() {
	open.value = !open.value;
	if (wgp.value.save) {
		sessionStorage.setItem(wgp.value.save, open.value ? "open" : "hidden");
	}
}

</script>

<style>
.reset-gutter {
	display: contents;
	--form-left-gutter: var(--next-left-gutter);
	--form-right-gutter: var(--next-right-gutter);
}

.fd-widget-group-closed {
	display: block;
	grid-column: 1 / -1;
	position: relative;

	&::after {
		content: "";
		position: absolute;
		width: calc(100% + var(--form-left-gutter) + var(--form-right-gutter));
		height: 1px;
		top: 9px;
		left: calc(-1 * var(--form-left-gutter));
		background-color: var(--form-border-color);
	}
}

.fd-widget-group {
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
	align-items: baseline;
	row-gap: calc(2 * var(--form-v-padding));
	column-gap: calc(2 * var(--form-h-padding));
	position: relative;
	--next-left-gutter: var(--form-left-gutter);
	--next-right-gutter: var(--form-right-gutter);
	transition: border-color 500ms;

	&.wg-separ-color,
	&.wg-separ-border,
	&.wg-separ-drawer {
		padding-block: var(--form-v-start-padding);
		border-radius: var(--form-v-start-padding);
		padding-left: calc((var(--form-left-gutter) - 1px) / 2);
		padding-right: calc((var(--form-right-gutter) - 1px) / 2);
		margin-left: calc(var(--form-left-gutter) / -2 - 1px);
		margin-right: calc(var(--form-right-gutter) / -2 - 1px);
		border: 1px solid var(--border-color);
		--next-left-gutter: calc((var(--form-left-gutter) - 1px) / 2);
		--next-right-gutter: calc((var(--form-right-gutter) - 1px) / 2);
	}

	&.wg-separ-color-sided,
	&.wg-separ-border-sided,
	&.wg-separ-drawer-sided {
		padding-block: var(--form-v-start-padding);
		padding-left: var(--form-left-gutter);
		padding-right: var(--form-right-gutter);
		margin-left: calc(-1 * var(--form-left-gutter));
		margin-right: calc(-1 * var(--form-right-gutter));
		border-top: 1px solid var(--border-color);
		border-bottom: 1px solid var(--border-color);
	}

	&.wg-separ-color,
	&.wg-separ-color-sided {
		background-color: var(--form-sbg);
	}

	&.wg-separ-color.wg-with-label,
	&.wg-separ-color-sided.wg-with-label {
		background: linear-gradient(to bottom, var(--form-bg), var(--form-sbg));
	}

	&.wg-separ-space {
	}

	&.wg-separ-space:not(:first-child) {
		margin-top: var(--form-v-start-padding);
	}

	&.wg-separ-space:not(:last-child) {
		margin-bottom: var(--form-v-start-padding);
	}

	&.wg-separ-drawer,
	&.wg-separ-drawer-sided {
		box-shadow: var(--drawer-shadow);
	}

	&.wg-with-label {
		margin-top: 9px;
		padding-top: 16px;
	}
}

.wg-label {
	--link-decoration: none;
	--link-decoration-hover: none;
	position: absolute;
	display: block;
	left: calc(var(--form-left-gutter) - 4px);
	top: -10px;
	padding: 0 4px;
	background-color: var(--form-bg);
	border-radius: 3px;
}

.wg-separ-space .wg-label {
	left: -4px;
}

.wg-label-close {
	--link-decoration: none;
	--link-decoration-hover: none;
	position:relative;
	left: -4px;
	padding-left: 4px;
	padding-right: 4px;
	z-index: 600;
	background-color: var(--form-bg);
}

</style>